import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkLossesAdjustmentsOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UkLossesAdjustmentsBoxes = {
  box45: number;
  box46: number;
  box47: number;
  box48: number;
};

export type UkLossesAdjustmentsCalculation = {
  gainsInYear: number;
  lossesInYear: number;
  netGainAfterCurrentLosses: number;
  broughtForwardAvailable: number;
  annualExemptAmount: number;
  usedFromBroughtForward: number;
  unusedBroughtForward: number;
  newLossesToCarryForward: number;
  totalCarryForward: number;
  taxableGain: number;
};

export type UkLossesAdjustmentsPriorYear = {
  taxYear: number;
  gainsGBP: number;
  lossesGBP: number;
  aea: number;
  netGainLoss: number;
  lossesCarriedForward: number;
};

export type UkLossesAdjustmentsResponse = {
  taxYear: number;
  boxes: UkLossesAdjustmentsBoxes;
  calculation: UkLossesAdjustmentsCalculation;
  priorYears: UkLossesAdjustmentsPriorYear[];
  aeaInteraction: string;
};

export const useUkLossesAdjustments = ({
  taxYear,
  enabled = true,
}: UseUkLossesAdjustmentsOptions): UseQueryResult<UkLossesAdjustmentsResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchLossesAdjustments = async (): Promise<UkLossesAdjustmentsResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/losses-adjustments",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_losses_adjustments", taxYear],
    queryFn: fetchLossesAdjustments,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
